import {
  useHistoryCustomBack,
  usePanelHistoryTop,
  useSectionHistoryTop,
} from 'weblite-router'
// React
import { useCallback, useEffect, useState } from 'react'
// Material
import { Box, Divider } from '@mui/material'
import { styled } from '@mui/material/styles'

import { LIMITS } from '@weblite/permission'
// Logic
import { useUser } from '$logic/users'
// Common
import { PageWithAppBar } from '$common/page-with-app-bar'
import { AppBarBackTitleConfirm } from '$common/app-bar/app-bar-back-title-confirm'
import { ContactInfoItem } from '$common/contact-info-item'
import { Label } from '$common/label'
import { TextInput } from '$common/input'
// Localization
import { t } from '$i18n'
import { showDiscardChangesConfirmModal } from '$comp/modals/confirmation-dialog'
import { closeEditContact } from '$logic/router'
import { addContacts, editContact } from '$logic/contacts/service'
import { showAddContactsSnackbars } from '$logic/contacts/helper'

const { userConstraints } = LIMITS

interface Props {
  userId: string
  addMode?: boolean
}

const StyledBox = styled(Box)(({ theme }) => ({
  paddingTop: '10px',
  backgroundColor: theme.palette.white,
  boxShadow: '0px 2px 3px rgba(0, 0, 0, 0.05)',
}))

export function EditContact({ userId, addMode = false }: Props) {
  const { user } = useUser(userId)
  const [firstname, setFirstname] = useState<string>('')
  const [lastname, setLastname] = useState<string>()

  const panelTop = usePanelHistoryTop('profile')
  const [topPage] = panelTop?.location?.split('/').slice(-1) ?? []
  const sectionTop = useSectionHistoryTop('left')
  useEffect(() => {
    if (!user?.id || topPage !== 'EDIT_CONTACT' || user.deletedAt) return
    setFirstname(user.firstname)
    setLastname(user.lastname)
  }, [
    user?.id,
    user?.deletedAt,
    user?.firstname,
    user?.lastname,
    topPage,
    sectionTop,
  ]) // Note: we should add user fields instead of user because user rerender every time

  const valueChanged =
    firstname !== user?.firstname || lastname !== user?.lastname

  const reset = useCallback(() => {
    setFirstname('')
    setLastname('')
  }, [])

  const onBackCallBack = useCallback(() => {
    reset()
    closeEditContact()
  }, [reset])

  useHistoryCustomBack(() => {
    onBackCallBack()
  }, [onBackCallBack])

  const onConfirm = useCallback(async () => {
    if (!user) return
    if (addMode) {
      const res = await addContacts([
        {
          firstname,
          lastname,
          phone: user.phone!,
        },
      ])
      const addedToContacts = Boolean(res.payload?.contacts[0].id)
      showAddContactsSnackbars(
        { [firstname + (lastname ? ' ' + lastname : '')]: [user.phone!] },
        {
          [user.phone!]: addedToContacts ? 'addedToContacts' : 'noAppAccount',
        },
      )
      onBackCallBack()
    } else if (valueChanged) {
      // TODO: Verify value
      // TODO: Show loading
      await editContact(user.id, {
        firstname,
        lastname,
        phone: user.phone!,
      })
      onBackCallBack()
    }
  }, [user, firstname, lastname, onBackCallBack, addMode, valueChanged])

  const onBack = useCallback(() => {
    if (addMode || valueChanged) {
      showDiscardChangesConfirmModal(onBackCallBack, onConfirm)
    } else onBackCallBack()
  }, [onBackCallBack, onConfirm, valueChanged, addMode])

  useHistoryCustomBack(() => {
    onBack()
  }, [onBack])
  return (
    <PageWithAppBar
      appBar={
        <AppBarBackTitleConfirm
          title={
            user?.isContact
              ? t('contacts.editContact')
              : t('contacts.newContact')
          }
          onBack={onBack}
          onConfirm={onConfirm}
          confirmDisabled={!firstname || (!addMode && !valueChanged)}
        />
      }
    >
      <StyledBox>
        <ContactInfoItem userId={userId} />

        <Divider sx={{ m: '10px 0' }} />

        <Box sx={{ padding: '0 20px' }}>
          <Label title={t('user.name')} required />
          <TextInput
            placeholder={t('user.enterName')}
            value={firstname}
            onChange={setFirstname}
            maxLength={userConstraints.firstname.maxLength}
          />
          <Label title={t('user.lastName')} required={false} />
          <TextInput
            placeholder={t('user.enterLastName')}
            value={lastname || ''}
            onChange={setLastname}
            maxLength={userConstraints.lastname.maxLength}
          />
        </Box>
      </StyledBox>
    </PageWithAppBar>
  )
}
